import { useEffect, useRef } from 'react';
import { CAMERA } from '../config/constants';
import { regionAround } from '../lib/geo';
import { mapController } from '../map/mapController';
import { useAppStore } from '../store/appStore';

/**
 * LocationManager port: request one fix at ~100 m accuracy when the map first
 * mounts, store it, and animate the camera once to a 20 km region around the
 * user. The camera never auto-recenters again (CombinedMapView.swift 516-526).
 */
export function useUserLocation(mapReady: boolean): void {
  const setUserLocation = useAppStore((s) => s.setUserLocation);
  const movedRef = useRef(false);

  useEffect(() => {
    if (!mapReady || !('geolocation' in navigator)) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        if (!movedRef.current) {
          movedRef.current = true;
          mapController.showRegion(regionAround(loc, CAMERA.initialUserZoomMeters));
        }
      },
      () => {
        // Denied or unavailable: the "center on user" control stays disabled.
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 20_000 },
    );
    return () => {
      cancelled = true;
    };
  }, [mapReady, setUserLocation]);
}
